import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
import { AppErrors, ConflictError, NotFoundError } from '@fitness-force/errors';
import { environment } from 'apps/ff-frontend/src/environments/environment';
import { PaymentGatewaySupported } from '@fitness-force/enum';
import { TENANT_MODEL } from 'libs/models/src/lib/tenant';
import { TimeconversionService } from './common/timeconversion.service';

@Injectable({
  providedIn: 'root',
})
export class TenantService {
  private isTenantPresale = new BehaviorSubject<boolean>(false);
  private tenantStartDate: Date | null = null;
  isPresaleTenant$ = this.isTenantPresale.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private timeService: TimeconversionService
  ) {}
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  GetTenantList(): Observable<any> {
    return this.http
      .get(environment.BASE_URL + CONST_URL_SEGEMENTS.TENANT.GET_TENANT_LIST, {
        headers: this.httpOptions.headers,
      })
      .pipe(
        map((res: any) => {
          if (res) {
            return res;
          } else {
            return null;
          }
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  GetTenantById(BASE_URL: string, tenantId: number): Observable<any> {
    return this.http
      .get(
        BASE_URL + CONST_URL_SEGEMENTS.TENANT.GET_TENANTBYID + '/' + tenantId,
        { headers: this.httpOptions.headers }
      )
      .pipe(
        map((res: any) => {
          if (res) {
            return res;
          } else {
            return null;
          }
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  CreateTenant(createTenantPayload: any): Observable<any> {
    return this.http
      .post(
        `${environment.BASE_URL}/tenants`,
        createTenantPayload,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          if (error.status === 409) {
            return throwError(() => new ConflictError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  GetTenantPosDevicesById(
    tenantId: number,
    paymentGateway: PaymentGatewaySupported
  ): Observable<any> {
    const url = `${environment.BASE_URL}/tenants/${tenantId}/pgw_config_master/ListOnlinePOS/${paymentGateway}`;
    return this.http.get(url, { headers: this.httpOptions.headers }).pipe(
      map((res: any) => {
        if (res) {
          return res;
        } else {
          return null;
        }
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  ActiveTenant(tenantId: number) {
    return this.http
      .post(
        `${environment.BASE_URL}/tenants/${tenantId}/mark_as_active`,
        {},
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  InActiveTenant(tenantId: number) {
    return this.http
      .post(
        `${environment.BASE_URL}/tenants/${tenantId}/mark_as_inactive`,
        {},
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  patchTenant(tenantId: number, data: any) {
    return this.http
      .patch(
        `${environment.BASE_URL}/tenants/${tenantId}`,
        data,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  updateTenantPresaleStatus(res: TENANT_MODEL): void {
    if (res) {
      this.isTenantPresale.next(res.status.toLowerCase() === 'presales');
      if (this.isTenantPresale) {
        this.tenantStartDate = this.timeService.ConvertUTCToLocalDateTime(
          res.tenant_start_date
        );
      }
    }
  }

  getFranchiseeList(): Observable<any> {
    return this.http.get(`${environment.BASE_URL}/franchisegroup`, { headers: this.httpOptions.headers }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getContactList(params: any): Observable<any> {
    let parsedParams = new HttpParams()
    if(params != null)
    {
      params.forEach((p: any) => {
        parsedParams = parsedParams.append(p.name, p.value);
      });
    }
    return this.http.get(`${environment.BASE_URL}/tenants/contacts`, { headers: this.httpOptions.headers, params:parsedParams }).pipe(
      map((res: any) => { 
        return res;
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getTenantStartDate(): Date | null {
    return this.tenantStartDate;
  }

  IsPresaleTenant(): boolean {
    return this.isTenantPresale.value;
  }
}
